import type { DivIcon } from "leaflet";
import { escapeHtml } from "../markdownClasses";

export type Leaflet = typeof import("leaflet");

export type MapCoordinates = {
  latitude: number;
  longitude: number;
};

export type MapMarker = MapCoordinates & {
  id: string;
  label?: string;
};

export type TileMapSkin = {
  surface: string;
  pin: string;
  pinSelected: string;
  self: string;
};

export const DEFAULT_TILE_MAP_SKIN: TileMapSkin = {
  surface: "h-full w-full",
  pin: "block h-3.5 w-3.5 rounded-full border-2 border-white bg-blue-600 shadow-md dark:border-neutral-900",
  pinSelected: "block h-5 w-5 rounded-full border-2 border-white bg-amber-500 shadow-lg dark:border-neutral-900",
  self: "block h-3 w-3 rounded-full border-2 border-white bg-sky-500 shadow-md dark:border-neutral-900",
};

export const DEFAULT_SELF_LABEL = "Your location";

export const PIN_MARKER_CLASS = "tile-map-pin";

export const SELF_MARKER_CLASS = "tile-map-self";

export type TileSource = {
  url: string;
  attribution: string;
};

export const TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

export const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors';

export const OSM_TILES: TileSource = { url: TILE_URL, attribution: TILE_ATTRIBUTION };

const CARTO_ATTRIBUTION = `${TILE_ATTRIBUTION} &copy; <a href="https://carto.com/attributions" target="_blank" rel="noreferrer">CARTO</a>`;

/** Muted basemaps: far less colour and label noise than the default OSM raster. CARTO stamps a watermark over every tile served without a key. */
const cartoTiles = (style: string, key: string | null): TileSource => ({
  url: `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}{r}.png${key === null ? "" : `?key=${encodeURIComponent(key)}`}`,
  attribution: CARTO_ATTRIBUTION,
});

export const cartoLightTiles = (key: string | null = null): TileSource => cartoTiles("light_all", key);

export const cartoDarkTiles = (key: string | null = null): TileSource => cartoTiles("dark_all", key);

export const MAX_TILE_ZOOM = 19;

export const FRAME_MAX_ZOOM = 16;

export const FRAME_PADDING: [number, number] = [36, 36];

export const EMPTY_CENTER: [number, number] = [0, 0];

export const EMPTY_ZOOM = 1;

const ICON_BOX = 32;

export const loadLeaflet = async (): Promise<Leaflet> => {
  const module = (await import("leaflet")) as Leaflet & { default?: Leaflet };
  return module.default ?? module;
};

export const toLatLng = (coordinates: MapCoordinates): [number, number] => [coordinates.latitude, coordinates.longitude];

export const markerIcon = (leaflet: Leaflet, className: string, marker: string): DivIcon =>
  leaflet.divIcon({
    className: marker,
    html: `<span class="flex h-full w-full items-center justify-center"><span class="${escapeHtml(className)}"></span></span>`,
    iconSize: [ICON_BOX, ICON_BOX],
    iconAnchor: [ICON_BOX / 2, ICON_BOX / 2],
  });

export const markersKey = (markers: readonly MapMarker[], self: MapCoordinates | null, selectedMarkerId: string | null, skin: TileMapSkin): string =>
  [framingKey(markers, self), selectedMarkerId ?? "", skin.pin, skin.pinSelected, skin.self].join("~");

export const framingKey = (markers: readonly MapMarker[], self: MapCoordinates | null): string =>
  [
    ...markers.map((marker) => `${marker.id}:${marker.latitude},${marker.longitude}:${marker.label ?? ""}`),
    self ? `${self.latitude},${self.longitude}` : "",
  ].join("|");
